import React from 'react';

import { BookingEditMode, BookingCreateMode } from '@go-client/pages/GroupsHome';
import { calculateTimeDifference, calculateTimeOffset } from '@go-shared/lib/time';
import {
  GroupBooking,
  VenueRequest,
  VenueRequestStatus,
  BareVenueRequest,
  NewGroupBooking,
  GroupSubgroup,
  VenueRequestFile,
  BareGroupBooking,
  BareSimpleGroupBooking,
  Resource,
} from '@go-shared/types/groupBooking';
import { Property } from '@go-shared/types/property';
import { BareSimpleSailing, BareSimpleSailingDayBundle } from '@go-shared/types/sailing';
import { VenueConfiguration, VenueTemplate } from '@go-shared/types/venueTemplate';
import { ObjectId } from 'bson';
import {
  update as updateGroupBooking,
  create as createGroupBooking,
  getGroupBookingsByProperty,
  addComment,
  getSimpleGroupBookingsByProperty,
} from '../../../lib/api/groupBooking';
import {
  bare as bareGroupBooking,
  populate as populateGroupBooking,
} from '@go-client/lib/api/groupBooking';
import { Time } from '@go-shared/types/time';
import { getSimpleSailingsByProperty } from '@go-client/lib/api/itinerary';
import { getUsers } from '@go-client/lib/api/user';
import {
  getVenueTemplatesByPropertyId,
  populate as populateVenue,
} from '@go-client/lib/api/venueTemplate';
import { capitalize } from '@go-shared/lib/string';
import { User } from '@go-shared/types/user';
import { BrandGroupType } from '@go-shared/types/brand';
import { findVenueRequestDifferences } from '@go-shared/lib/groupBooking';
import { groupResourceClient } from '@go-client/lib/api/v2/groupResource';
import { safeFetch } from '@go-client/lib/api';
import { z } from 'zod';
import { GroupResource, GroupResourceSchema } from '@go-shared/types/zod/groupResource';
import { BufferTime } from '@go-shared/types/zod/groupEvent';

export interface GroupBookingFormWorkflow {
  prependNode?: React.ReactNode;
}

export interface GroupBookingDataBundle {
  groupBookings: GroupBooking[];
  simpleGroupBookings: BareSimpleGroupBooking[];
  simpleSailings: BareSimpleSailing[];
  venueTemplates: VenueTemplate[];
  allUsers: User[];
  groupResources: GroupResource[];
  auxData?: GroupBookingDataBundle;
}

export interface BookingForm {
  name: string;
  startTime: Time | undefined;
  endTime: Time | undefined;
  numGuests: number;
  prepaidPackages: string[];
  dining: string;
  venueConfiguration: VenueConfiguration | undefined;
  notes: string;
  files: VenueRequestFile[];
  _dirty: boolean;
  resources: Resource[];
  bufferTime: BufferTime;
}

export interface GroupForm {
  name: string;
  contact: string;
  phone: string;
  email: string;
  notes: string;
  groupId: string;
  groupGuideName: string;
  groupGuideCabin: string;
  size: number;
  groupType?: BrandGroupType;
  hospitalityDesk: boolean;
  salesOffice: string;
  cabinDrops: string;
  diningPreference: string;
  guestExperience: string;
  prepaidPackages: string;
  embark: string;
  debark: string;
  files: VenueRequestFile[];
  _dirty: boolean;
}

export interface SubgroupForm {
  label: string;
  name: string;
  notes: string;
  groupGuideName: string;
  groupGuideCabin: string;
  attendees: number;
  files: VenueRequestFile[];
  _dirty: boolean;
}

export interface GroupResourceForm {
  name: string;
}

export const ResourceDataSchema = z.object({
  resourceTypes: z.string().array(),
  resources: z.array(GroupResourceSchema),
});

export type ResourceData = z.infer<typeof ResourceDataSchema>;

export function generateDefaultBookingForm(request?: VenueRequest): BookingForm {
  // populate resources with name and type from resource data if available
  // this is to avoid having to fetch resources when editing a booking
  // since we already have them in the data bundle
  if (request && request.resources) {
    request.resources = request.resources.map(r => ({
      ...r,
      name: r.name,
      resourceType: r.resourceType,
      archived: r.archived,
    }));
  }

  return {
    name: request?.name ?? '',
    startTime: request ? request.startTime : undefined,
    endTime: request ? calculateTimeOffset(request.startTime, request.duration).time : undefined,
    numGuests: request?.numGuests ?? 1,
    prepaidPackages: request?.packages ?? [],
    dining: request?.dining ?? '',
    venueConfiguration: request?.venueConfig ?? undefined,
    notes: request?.notes ?? '',
    files: request?.files ?? [],
    resources: request?.resources ?? [],
    bufferTime: request?.bufferTime ?? { setUpTime: 0, breakdownTime: 0 },
    _dirty: false,
  };
}

export function generateDefaultGroupForm(booking?: GroupBooking): GroupForm {
  return {
    name: booking?.party.name ?? '',
    contact: booking?.party.contact ?? '',
    phone: booking?.party.phone ?? '',
    email: booking?.party.email ?? '',
    notes: booking?.party.notes ?? '',
    groupId: booking?.party.groupId ?? '',
    groupGuideName: booking?.party.groupGuideName ?? '',
    groupGuideCabin: booking?.party.groupGuideCabin ?? '',
    size: booking?.party.size ?? 1,
    groupType: booking?.party.groupType ?? undefined,
    salesOffice: booking?.salesOffice ?? '',
    hospitalityDesk: booking?.party.hospitalityDesk ?? false,
    cabinDrops: booking?.party.cabinDrops ?? '',
    diningPreference: booking?.party.diningPreference ?? '',
    guestExperience: booking?.party.guestExperience ?? '',
    prepaidPackages: booking?.party.prepaidPackages ?? '',
    embark: booking?.party.embark ?? '',
    debark: booking?.party.debark ?? '',
    files: booking?.party.files ?? [],
    _dirty: false,
  };
}

export function generateDefaultSubgroupForm(subgroup?: GroupSubgroup): SubgroupForm {
  return {
    label: subgroup?.label ?? '',
    name: subgroup?.name ?? '',
    notes: subgroup?.notes ?? '',
    groupGuideName: subgroup?.groupGuideName ?? '',
    groupGuideCabin: subgroup?.groupGuideCabin ?? '',
    attendees: subgroup?.attendees ?? 0,
    files: subgroup?.files ?? [],
    _dirty: false,
  };
}

function makeNewGroupBooking(
  groupDetailsForm: GroupForm,
  requests: BareVenueRequest[],
  propertyId: string
) {
  const result: NewGroupBooking = {
    property: propertyId,
    date: new Date(),
    party: {
      _id: new ObjectId().toHexString(),
      name: groupDetailsForm.name,
      contact: groupDetailsForm.contact,
      phone: groupDetailsForm.phone,
      email: groupDetailsForm.email,
      notes: groupDetailsForm.notes,
      groupId: groupDetailsForm.groupId,
      groupGuideName: groupDetailsForm.groupGuideName,
      groupGuideCabin: groupDetailsForm.groupGuideCabin,
      size: groupDetailsForm.size, // number of guests in party
      groupType: groupDetailsForm.groupType,
      cabinDrops: groupDetailsForm.cabinDrops,
      diningPreference: groupDetailsForm.diningPreference,
      guestExperience: groupDetailsForm.guestExperience,
      prepaidPackages: groupDetailsForm.prepaidPackages,
      embark: groupDetailsForm.embark,
      debark: groupDetailsForm.debark,
      files: groupDetailsForm.files,
    },
    salesOffice: groupDetailsForm.salesOffice,
    requests,
    subgroups: [],
  };

  return result;
}

interface SubmitBookingFormProps {
  property: Property;
  user: User;
  type: BookingEditMode['type'] | BookingCreateMode['type'];
  selectedGroup?: GroupBooking;
  selectedVenue: VenueTemplate;
  selectedSailingBundle?: BareSimpleSailingDayBundle;
  bookingToReplace?: VenueRequest;
  bookingDetailsForm: BookingForm;
  status: VenueRequestStatus;
  newGroup: boolean;
  groupDetailsForm?: GroupForm;
  resources?: Resource[];
}

export async function submitBookingForm(props: SubmitBookingFormProps) {
  const { bookingDetailsForm, groupDetailsForm } = props;
  console.log('SUBMIT BOOKING FORM', props);
  const bareGroup = props.selectedGroup ? bareGroupBooking(props.selectedGroup) : undefined;

  const startTime = bookingDetailsForm.startTime;
  const endTime = bookingDetailsForm.endTime;

  if (!startTime || !endTime) {
    throw new Error(`Could not submit request due to a missing data piece.`);
  }

  let duration = calculateTimeDifference(endTime, startTime);

  switch (props.type) {
    case 'edit':
      // users wont actually run into these in theory: they are more for developers to prevent
      // calling this function without the adequate information beforehand
      if (!bareGroup) {
        throw new Error('Tried to edit an existing booking without a group selected!');
      }
      if (!props.selectedSailingBundle) {
        throw new Error('Tried to edit a group booking without selecting itinerary!');
      }

      let existingReqIdx = bareGroup?.requests.findIndex(
        rq => rq._id === props.bookingToReplace?._id
      );
      if (existingReqIdx < 0) {
        throw new Error(
          'Tried to change an existing group booking without providing a valid booking to replace!'
        );
      }

      const existingReq = bareGroup.requests[existingReqIdx];
      const newReq = {
        ...existingReq,
        venue: props.selectedVenue._id,
        date: props.selectedSailingBundle.day.date,
        dayIndex: props.selectedSailingBundle.dayIndex,
        name: bookingDetailsForm.name,
        numGuests: bookingDetailsForm.numGuests,
        startTime,
        duration,
        status: props.status,
        resources: bookingDetailsForm.resources,
        packages: bookingDetailsForm.prepaidPackages,
        dining: bookingDetailsForm.dining,
        venueConfig: bookingDetailsForm.venueConfiguration?._id,
        notes: bookingDetailsForm.notes,
        files: bookingDetailsForm.files,
        bufferTime: bookingDetailsForm.bufferTime,
      };

      bareGroup.requests.splice(existingReqIdx, 1, {
        ...newReq,
        comments: [
          ...existingReq.comments,
          {
            user: {
              _id: props.user._id,
              firstName: props.user.firstName,
              lastName: props.user.lastName,
              email: props.user.email,
            },
            date: new Date(),
            message: `Changed ${findVenueRequestDifferences(existingReq, newReq).join(', ')}`,
          },
        ],
      });

      await updateGroupBooking(bareGroup);
      break;
    case 'create':
      if (!props.selectedSailingBundle) {
        throw new Error('Tried to create a group booking without selecting itinerary!');
      }
      const newRequest: BareVenueRequest = {
        _id: new ObjectId().toHexString(),
        venue: props.selectedVenue._id,
        createdBy: props.user._id,
        date: props.selectedSailingBundle.day.date,
        dayIndex: props.selectedSailingBundle.dayIndex,
        status: props.status,
        name: bookingDetailsForm.name,
        numGuests: bookingDetailsForm.numGuests,
        startTime,
        duration,
        resources: bookingDetailsForm.resources,
        packages: bookingDetailsForm.prepaidPackages,
        dining: bookingDetailsForm.dining,
        venueConfig: bookingDetailsForm.venueConfiguration?._id,
        notes: bookingDetailsForm.notes,
        files: bookingDetailsForm.files,
        comments: [],
        bufferTime: bookingDetailsForm.bufferTime,
      };

      if (props.newGroup) {
        if (!groupDetailsForm) {
          throw new Error(
            `Tried to create a booking with a new group without providing new group details!`
          );
        }

        const newGroupBooking = makeNewGroupBooking(
          groupDetailsForm,
          [newRequest],
          props.property._id
        );
        await createGroupBooking(newGroupBooking, props.property._id);
      } else {
        if (!bareGroup) {
          throw new Error('Tried to add a booking to an existing group without a group selected!');
        }

        bareGroup.requests.push(newRequest);
        await updateGroupBooking(bareGroup);
      }
      break;
  }
}

interface SubmitGroupFormProps {
  selectedGroup?: GroupBooking;
  propertyId: string;
  groupForm: GroupForm;
}

export async function submitGroupForm(props: SubmitGroupFormProps) {
  const { groupForm: groupDetailsForm, selectedGroup, propertyId } = props;

  const party = {
    name: groupDetailsForm.name,
    groupId: groupDetailsForm.groupId,
    contact: groupDetailsForm.contact,
    groupGuideName: groupDetailsForm.groupGuideName,
    groupGuideCabin: groupDetailsForm.groupGuideCabin,
    groupType: groupDetailsForm.groupType,
    size: groupDetailsForm.size,
    cabinDrops: groupDetailsForm.cabinDrops,
    diningPreference: groupDetailsForm.diningPreference,
    guestExperience: groupDetailsForm.guestExperience,
    prepaidPackages: groupDetailsForm.prepaidPackages,
    hospitalityDesk: groupDetailsForm.hospitalityDesk,
    embark: groupDetailsForm.embark,
    debark: groupDetailsForm.debark,
    notes: groupDetailsForm.notes,
    files: groupDetailsForm.files,
  };

  if (selectedGroup) {
    const bareGroup = bareGroupBooking(selectedGroup);
    bareGroup.party = {
      ...bareGroup.party,
      ...party,
    };
    bareGroup.salesOffice = groupDetailsForm.salesOffice;
    await updateGroupBooking(bareGroup);
  } else {
    const bareGroup = makeNewGroupBooking(groupDetailsForm, [], propertyId);
    bareGroup.party = {
      ...bareGroup.party,
      ...party,
    };
    bareGroup.salesOffice = groupDetailsForm.salesOffice;
    await createGroupBooking(bareGroup, props.propertyId);
  }
}

interface SubmitSubgroupFormProps {
  selectedGroup: GroupBooking;
  selectedSubgroup: GroupSubgroup;
  subgroupForm: SubgroupForm;
}

export async function submitSubgroupForm(props: SubmitSubgroupFormProps) {
  const bareGroup = bareGroupBooking(props.selectedGroup);
  let subgroupIdx = bareGroup.subgroups.findIndex(sg => sg._id === props.selectedSubgroup._id);
  if (subgroupIdx < 0) return;

  const { subgroupForm: subgroupDetailsForm } = props;

  bareGroup.subgroups[subgroupIdx] = {
    ...bareGroup.subgroups[subgroupIdx],
    name: subgroupDetailsForm.name,
    groupGuideName: subgroupDetailsForm.groupGuideName,
    groupGuideCabin: subgroupDetailsForm.groupGuideCabin,
    attendees: subgroupDetailsForm.attendees,
    notes: subgroupDetailsForm.notes,
    files: subgroupDetailsForm.files,
  };

  await updateGroupBooking(bareGroup);
}

export async function bulkBookingStatusChange(
  groupBookings: Array<{ group: BareGroupBooking; booking: BareVenueRequest }>,
  user: User,
  status: VenueRequestStatus
) {
  const comment = {
    message: capitalize(status),
    date: new Date(),
    user: {
      _id: user._id,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
    },
  };

  await Promise.all(
    groupBookings.map(gbk =>
      addComment(gbk.group, gbk.booking, {
        ...comment,
        status,
      })
    )
  );
}

export async function fetchDataBundle(
  property: Property,
  since?: string,
  until?: string
): Promise<GroupBookingDataBundle> {
  const bareGroupBookings = await getGroupBookingsByProperty(property._id, since, until);

  const { minDate, maxDate } = bareGroupBookings
    .flatMap(gb => gb.requests)
    .reduce<{
      minDate: Date | null;
      maxDate: Date | null;
    }>(
      (acc, request) => {
        const currentDate = new Date(request.date);

        if (!acc.minDate || currentDate < acc.minDate) {
          acc.minDate = currentDate;
        }
        if (!acc.maxDate || currentDate > acc.maxDate) {
          acc.maxDate = currentDate;
        }

        return acc;
      },
      { minDate: null, maxDate: null }
    );

  const [bareSimpleGroupBookings, bareSimpleSailings, bareVenueTemplates, allUsers] =
    await Promise.all([
      getSimpleGroupBookingsByProperty(property._id),
      getSimpleSailingsByProperty(property._id, minDate?.toISOString(), maxDate?.toISOString()),
      getVenueTemplatesByPropertyId(property._id),
      getUsers(property._id),
    ]);

  const { resources: groupResources } = await safeFetch(
    groupResourceClient.getResourcesAndTypes({
      params: { brandId: property.brand._id },
    }),
    ResourceDataSchema
  );

  const venueTemplates = bareVenueTemplates.map(v => populateVenue(v, property));

  const groupBookings = bareGroupBookings.map(bgb =>
    populateGroupBooking(bgb, property, allUsers, venueTemplates, groupResources)
  );

  return {
    groupBookings,
    simpleGroupBookings: bareSimpleGroupBookings,
    simpleSailings: bareSimpleSailings,
    venueTemplates,
    allUsers,
    groupResources,
  };
}

// this one fetches for a list of properties instead of only one (hence the multi)
export async function fetchMultiDataBundle(property: Property[], since?: string, until?: string) {
  const multiBundle: GroupBookingDataBundle = {
    groupBookings: [],
    simpleGroupBookings: [],
    simpleSailings: [],
    venueTemplates: [],
    allUsers: [],
    groupResources: [],
  };

  for (let prop of property) {
    const bundle = await fetchDataBundle(prop, since, until);

    multiBundle.groupBookings = multiBundle.groupBookings.concat(bundle.groupBookings);
    multiBundle.simpleGroupBookings = multiBundle.simpleGroupBookings.concat(
      bundle.simpleGroupBookings
    );
    multiBundle.simpleSailings = multiBundle.simpleSailings.concat(bundle.simpleSailings);
    multiBundle.venueTemplates = multiBundle.venueTemplates.concat(bundle.venueTemplates);
    multiBundle.allUsers = multiBundle.allUsers.concat(bundle.allUsers);
  }

  return multiBundle;
}

export { default as buildCreateBookingWorkflow } from './CreateBookingWorkflow';
export { default as buildEditBookingWorkflow } from './EditBookingWorkflow';
export { default as buildEditGroupWorkflow } from './EditGroupWorkflow';
export { default as buildEditSubgroupWorkflow } from './EditSubgroupWorkflow';
